import { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import {
  initialsSelector,
  nameSelector,
  userSelector,
} from '../../redux/selectors/user';
import { useAppDispatch, useAppSelector } from '../../utils/redux';
import Avatar from '@mui/material/Avatar';
import EnhancedTableToolbar from '../Tables/EnhancedTableToolbar';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { getNotes, postNote } from '../../redux/actions/deviceAction';
import { buildingSelector } from '../../redux/selectors/building';
import { notesSelector } from '../../redux/selectors/device';
import dayjs from 'dayjs';

export default function NotesCard() {
  const [input, setInput] = useState('');
  const { t } = useTranslation();
  const currentUser = useAppSelector(userSelector);
  const initials = useAppSelector(initialsSelector);
  const userName = useAppSelector(nameSelector);
  const building = useAppSelector(buildingSelector);
  const notes: any = useAppSelector(notesSelector);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (building?.id) {
      dispatch(getNotes({ buildingId: building.id }));
    }
  }, [building?.id, dispatch]);

  const submitComment = async () => {
    const data = {
      body: input,
    };
    dispatch(postNote({ buildingId: building?.id, data }));
    if (!input.trim()) return;

    setInput('');
  };

  const createInitials = (name: string) => {
    const words = name.split(' ');
    const initials = words.map((word: string) => word.charAt(0)).join('');
    return initials.toUpperCase();
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Paper
        sx={{
          width: '100%',
          mb: 2,
          backgroundColor: '#F8F9FB',
          border: '1px solid #D1D9E2',
          borderRadius: '10px',
          boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
          padding: '10px',
        }}
      >
        <EnhancedTableToolbar
          title={t('devices-notes')}
          description={t('devices-notes-description')}
        />

        <ListContainer>
          {notes.map((note: any) => (
            <CommentRow key={note.id}>
              <Line />

              <SingleCommentContainer>
                <div>
                  <Avatar
                    sx={{
                      height: '20px',
                      width: '20px',
                      fontSize: '14px',
                    }}
                  >
                    {createInitials(note.user.name)}
                  </Avatar>
                  <CentralBar />
                </div>
                <div>
                  <Header>
                    <InformationContainer>
                      <AuthorName>{note.user.name}</AuthorName>
                      <DateContainer>
                        {dayjs(note.create_at).format('MMM DD')}
                      </DateContainer>
                    </InformationContainer>
                  </Header>

                  <Text>{note.body}</Text>
                </div>
              </SingleCommentContainer>
            </CommentRow>
          ))}
        </ListContainer>

        <InputContainer>
          <Input
            placeholder={t('note-placeholder')}
            value={input}
            onChange={(e) => setInput(e.target.value)}
          />
          <Button onClick={submitComment}>{t('note-button')}</Button>
        </InputContainer>
      </Paper>
    </Box>
  );
}

const Container = styled('div')`
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  font-family: sans-serif;
`;
const ListContainer = styled('div')`
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 25px;
`;
const CommentRow = styled('div')`
  display: flex;
  flex-direction: row;
  gap: 15px;
  align-items: flex-start;
`;
const SingleCommentContainer = styled('div')`
  display: flex;
  gap: 10px;
`;
const Line = styled('div')`
  width: 3px;
  background: #e24c4c;
  border-radius: 5;
  height: 100%;
`;
const Header = styled('div')`
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
  margin-bottom: 5;
`;
const InformationContainer = styled('div')`
  display: flex;
  align-items: center;
  gap: 10px;
`;
const AuthorName = styled('div')`
  font-weight: 600;
  font-size: 14px;
`;
const DateContainer = styled('div')`
  font-size: 12px;
  color: #999;
`;
const Text = styled('div')`
  font-size: 14px;
  margin-top: 5px;
`;
const InputContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
const Input = styled('textarea')`
  width: 100%;
  min-height: 60px;
  padding: 10px;
  font-size: 14px;
  border-radius: 6px;
  border: 1px solid #ccc;
`;
const Button = styled('button')`
  align-self: flex-end;
  padding: 6px 14px;
  border-radius: 6px;
  background: #e24c4c;
  color: #fff;
  border: none;
  cursor: pointer;
`;

const CentralBar = styled('div')`
  width: 2px;
  height: -webkit-fill-available;
  margin-block: 6px;
  background: #99999947;
  border-radius: 4px;
  justify-self: center;
`;
